import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CheckIcon, CloseIcon, TalogentFooterBar } from '@platform/ui-kit';
import { ApiError, api } from '../api';

/**
 * Forced password change.
 *
 * Shown instead of the portal when `mustChangePassword` is set — an administrator
 * handed this user a password, so somebody other than them knows it. There is
 * deliberately no way past this screen except changing the password or signing out.
 */

interface Rule {
  label: string;
  test: (value: string) => boolean;
}

// Mirrors DEFAULT_PASSWORD_POLICY. Shown live so the user is not guessing at what
// the server will accept; the server remains the authority and re-checks.
const RULES: Rule[] = [
  { label: 'At least 12 characters', test: (v) => v.length >= 12 },
  { label: 'An uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'A lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'A number', test: (v) => /[0-9]/.test(v) },
];

const MESSAGES: Record<string, string> = {
  invalid_current_password: 'Your current password is not correct.',
  reused: 'That password was used recently. Choose a different one.',
  policy: 'That password does not meet the requirements below.',
};

export interface ChangePasswordPageProps {
  displayName: string;
  onChanged: () => void;
  onSignOut: () => void;
}

export function ChangePasswordPage({
  displayName,
  onChanged,
  onSignOut,
}: ChangePasswordPageProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const rulesMet = RULES.every((rule) => rule.test(newPassword));
  const matches = newPassword.length > 0 && newPassword === confirm;
  const canSubmit = Boolean(currentPassword) && rulesMet && matches && !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api.changePassword(currentPassword, newPassword);
      onChanged();
    } catch (err) {
      const key = err instanceof ApiError ? err.message : '';
      setError(MESSAGES[key] ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            <Typography variant="h6" component="h1">
              Choose a new password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayName}, your password was set by an administrator. Please replace it before
              continuing.
            </Typography>
          </Stack>

          <form onSubmit={submit}>
            <Stack spacing={2.5}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <TextField
                label="Current password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
              />
              <TextField
                label="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                fullWidth
              />
              <TextField
                label="Confirm new password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                fullWidth
                error={confirm.length > 0 && !matches}
              />

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Requirements
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {RULES.map((rule) => {
                    const met = rule.test(newPassword);
                    return (
                      <Stack key={rule.label} direction="row" spacing={1} alignItems="center">
                        {met ? (
                          <CheckIcon fontSize="small" color="success" />
                        ) : (
                          <CloseIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        )}
                        <Typography
                          variant="body2"
                          color={met ? 'text.primary' : 'text.secondary'}
                        >
                          {rule.label}
                        </Typography>
                      </Stack>
                    );
                  })}
                  <Stack direction="row" spacing={1} alignItems="center">
                    {matches ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <CloseIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    )}
                    <Typography
                      variant="body2"
                      color={matches ? 'text.primary' : 'text.secondary'}
                    >
                      Both entries match
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button type="submit" variant="contained" disabled={!canSubmit} sx={{ height: 44 }}>
                  {busy ? 'Saving…' : 'Set password'}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={onSignOut} disabled={busy}>
                  Sign out
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
      {/* This screen is OUTSIDE AppShell, so the shell's footer never reaches it.
          Rendered here so the credit is present before sign-in too. */}
      <TalogentFooterBar />
    </Box>
  );
}
